import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Check, X, Zap, Crown, Star, Tag, Percent, Calendar } from 'lucide-react'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

interface Plan {
  id: string
  slug: string
  name: string
  description: string
  price: number
  price_formatted: string
  currency: string
  interval: string
  is_featured: boolean
  trial_days: number
  features: Record<string, { label: string; enabled: boolean }>
  quotas: Record<string, number>
}

interface Subscription {
  id: string
  status: string
  is_active: boolean
  is_in_trial: boolean
  current_period_start: string
  current_period_end: string
  trial_ends_at: string | null
  days_remaining: number
  cancelled_at: string | null
  plan: {
    id: string
    name: string
    slug: string
    price: string
    price_formatted: string
  } | null
}

interface DiscountCalculation {
  code: string
  original_amount: number
  discounted_amount: number
  savings: number
  savings_formatted: string
  period_months: number
  is_period_discount: boolean
  discount_category: string
}

interface PeriodOption {
  months: number
  label: string
  discount_percent: number
  description: string
}

async function fetchPlans(): Promise<Plan[]> {
  const response = await fetch('/api/v1/subscriptions/plans', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
  })
  if (!response.ok) throw new Error('Failed to fetch plans')
  const data = await response.json()
  return data.data || []
}

async function fetchCurrentSubscription(): Promise<Subscription | null> {
  const response = await fetch('/api/v1/subscriptions/current', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
  })
  if (!response.ok) throw new Error('Failed to fetch subscription')
  const data = await response.json()
  return data.data || null
}

async function calculateDiscount(code: string, planId: string, periodMonths: number): Promise<DiscountCalculation> {
  const response = await fetch('/api/v1/subscriptions/coupons/calculate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
    },
    body: JSON.stringify({ code, plan_id: planId, period_months: periodMonths }),
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.message || 'Failed to calculate discount')
  }
  const data = await response.json()
  return data.data
}

const PERIOD_OPTIONS: PeriodOption[] = [
  { months: 1, label: 'Monthly', discount_percent: 0, description: 'Pay monthly' },
  { months: 3, label: 'Quarterly', discount_percent: 10, description: 'Save 10%' },
  { months: 6, label: 'Semi-Annual', discount_percent: 15, description: 'Save 15%' },
  { months: 12, label: 'Annual', discount_percent: 20, description: 'Save 20%' },
]

export default function SubscriptionPage() {
  const embedded = useDashboardEmbedded()
  const [couponCode, setCouponCode] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState<DiscountCalculation | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState(1)
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [couponError, setCouponError] = useState<string | null>(null)
  const [enableTrial, setEnableTrial] = useState(false)

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: fetchPlans,
  })

  const { data: subscription, isLoading: subscriptionLoading } = useQuery({
    queryKey: ['current-subscription'],
    queryFn: fetchCurrentSubscription,
  })

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !selectedPlanId) {
      setCouponError('Please select a plan first')
      return
    }

    setCouponError(null)
    try {
      const discount = await calculateDiscount(couponCode.trim(), selectedPlanId, selectedPeriod)
      setAppliedCoupon(discount)
      setCouponError(null)
    } catch (error: any) {
      setAppliedCoupon(null)
      setCouponError(error.message || 'Invalid coupon code')
    }
  }

  const handleClearCoupon = () => {
    setAppliedCoupon(null)
    setCouponCode('')
    setCouponError(null)
  }

  const handleCheckout = async (planId: string) => {
    setSelectedPlanId(planId)
    
    const response = await fetch('/api/v1/subscriptions/checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify({ 
        plan_id: planId,
        coupon_code: appliedCoupon?.code || undefined,
        period_months: selectedPeriod,
        enable_trial: enableTrial,
      }),
    })
    
    if (response.ok) {
      const data = await response.json()
      if (data.data?.checkout_url) {
        window.location.href = data.data.checkout_url
      }
    }
  }

  const handleManageBilling = async () => {
    const response = await fetch('/api/v1/subscriptions/portal', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    })
    
    if (response.ok) {
      const data = await response.json()
      if (data.data?.portal_url) {
        window.location.href = data.data.portal_url
      }
    }
  }

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your billing period.')) {
      return
    }
    
    await fetch('/api/v1/subscriptions/cancel', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify({ at_period_end: true }),
    })
    
    window.location.reload()
  }

  const calculatePrice = (plan: Plan, periodMonths: number): { original: number; discounted: number; savings: number } => {
    const original = plan.price * periodMonths
    let discounted = original
    let savings = 0

    if (appliedCoupon && selectedPlanId === plan.id) {
      discounted = appliedCoupon.discounted_amount
      savings = appliedCoupon.savings
    } else {
      // Apply period discount
      const periodOption = PERIOD_OPTIONS.find(p => p.months === periodMonths)
      if (periodOption && periodOption.discount_percent > 0) {
        savings = Math.round(original * periodOption.discount_percent / 100)
        discounted = original - savings
      }
    }

    return { original, discounted, savings }
  }

  if (plansLoading || subscriptionLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64 bg-muted rounded"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-8">
      {!embedded && (
        <div>
          <h1 className="text-3xl font-bold text-foreground">Subscription</h1>
          <p className="text-muted-foreground mt-1">
            Choose the plan that works best for you
          </p>
        </div>
      )}

      {/* Current Plan */}
      {subscription && subscription.plan && (
        <Card>
          <CardHeader>
            <CardTitle>Current Plan</CardTitle>
            <CardDescription>Your active subscription details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold">{subscription.plan.name}</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    subscription.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : subscription.status === 'trialing'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {subscription.status}
                  </span>
                </div>
                <p className="text-muted-foreground mt-1">
                  {subscription.plan.price_formatted}/month
                </p>
                {subscription.is_in_trial && subscription.trial_ends_at && (
                  <p className="text-sm text-blue-600 mt-2">
                    Trial ends: {new Date(subscription.trial_ends_at).toLocaleDateString()}
                  </p>
                )}
                {subscription.days_remaining > 0 && !subscription.cancelled_at && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {subscription.days_remaining} days remaining in billing period
                  </p>
                )}
                {subscription.cancelled_at && (
                  <p className="text-sm text-yellow-600 mt-1">
                    Cancels at end of billing period
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleManageBilling}>
                  Manage Billing
                </Button>
                {!subscription.cancelled_at && (
                  <Button variant="destructive" onClick={handleCancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period Selection and Coupon */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Options</CardTitle>
          <CardDescription>Choose your billing period and apply discounts</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Period Selection */}
          <div>
            <label className="text-sm font-medium mb-3 block">Billing Period</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {PERIOD_OPTIONS.map((option) => (
                <button
                  key={option.months}
                  type="button"
                  onClick={() => setSelectedPeriod(option.months)}
                  className={`p-4 border rounded-lg text-center transition-colors ${
                    selectedPeriod === option.months
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium">{option.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                  {option.discount_percent > 0 && (
                    <span className="text-xs text-green-600 font-medium mt-1 block">
                      {option.discount_percent}% off
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Coupon Input */}
          <div>
            <label className="text-sm font-medium mb-3 block flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Have a coupon code?
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter coupon code (e.g., WELCOME10)"
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value.toUpperCase())
                  setAppliedCoupon(null)
                  setCouponError(null)
                }}
                className="uppercase"
              />
              {appliedCoupon ? (
                <Button variant="outline" onClick={handleClearCoupon}>
                  Clear
                </Button>
              ) : (
                <Button onClick={handleApplyCoupon} disabled={!couponCode.trim()}>
                  Apply
                </Button>
              )}
            </div>
            {couponError && (
              <p className="text-sm text-red-600 mt-2">{couponError}</p>
            )}
            {appliedCoupon && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-800">
                      Coupon applied: {appliedCoupon.code}
                    </p>
                    <p className="text-sm text-green-600">
                      You save €{(appliedCoupon.savings / 100).toFixed(2)}!
                    </p>
                  </div>
                  <Percent className="w-5 h-5 text-green-600" />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Available Plans */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {plans?.map((plan) => {
            const { original, discounted, savings } = calculatePrice(plan, selectedPeriod)
            const hasDiscount = savings > 0

            return (
              <Card 
                key={plan.id}
                className={plan.is_featured ? 'border-primary shadow-lg' : ''}
              >
                {plan.is_featured && (
                  <div className="bg-primary text-white text-center py-1 text-sm font-medium rounded-t-lg flex items-center justify-center gap-1">
                    <Star className="w-3 h-3" /> Most Popular
                  </div>
                )}
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <div className="text-right">
                      <div className="flex items-baseline gap-1">
                        {hasDiscount && (
                          <span className="text-sm text-muted-foreground line-through">
                            €{(original / 100).toFixed(2)}
                          </span>
                        )}
                        <span className="text-3xl font-bold">€{(discounted / 100).toFixed(2)}</span>
                      </div>
                      <span className="text-muted-foreground text-sm">
                        /{selectedPeriod === 1 ? 'month' : `${selectedPeriod} months`}
                      </span>
                    </div>
                  </div>
                  <CardDescription>{plan.description}</CardDescription>
                  {hasDiscount && (
                    <p className="text-sm text-green-600 font-medium flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Save €{(savings / 100).toFixed(2)} ({Math.round(savings / original * 100)}% off)
                    </p>
                  )}
                  {plan.trial_days > 0 && (
                    <p className="text-sm text-blue-600 font-medium">
                      {plan.trial_days} day free trial
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {Object.entries(plan.features).map(([key, feature]) => (
                      <li key={key} className="flex items-center gap-2">
                        {feature.enabled ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <X className="w-4 h-4 text-gray-400" />
                        )}
                        <span className={feature.enabled ? 'text-foreground' : 'text-muted-foreground'}>
                          {feature.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {/* Free Trial Toggle */}
                  {plan.trial_days > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedPlanId === plan.id && enableTrial}
                          onChange={(e) => {
                            setSelectedPlanId(plan.id)
                            setEnableTrial(e.target.checked)
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-blue-800">
                          Start with {plan.trial_days}-day free trial
                        </span>
                      </label>
                    </div>
                  )}
                  <div className="mt-6">
                    {subscription?.plan?.slug === plan.slug ? (
                      <Button variant="outline" disabled className="w-full">
                        Current Plan
                      </Button>
                    ) : (
                      <Button 
                        className="w-full" 
                        onClick={() => handleCheckout(plan.id)}
                        variant={plan.is_featured ? 'default' : 'outline'}
                      >
                        {subscription ? 'Upgrade' : 'Subscribe'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>

      {/* Plan Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Plan Limits</CardTitle>
          <CardDescription>Quotas and limitations for each plan</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted rounded-lg">
              <Zap className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm text-muted-foreground">Gallery Images</p>
              <p className="text-xl font-bold">
                {plans?.[0]?.quotas['gallery.images'] || 0}
              </p>
              <p className="text-xs text-muted-foreground">Starter</p>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <Zap className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm text-muted-foreground">Gallery Images</p>
              <p className="text-xl font-bold">
                {plans?.[1]?.quotas['gallery.images'] || 0}
              </p>
              <p className="text-xs text-muted-foreground">Professional</p>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <Crown className="w-6 h-6 mx-auto mb-2 text-amber-500" />
              <p className="text-sm text-muted-foreground">Services</p>
              <p className="text-xl font-bold">
                {plans?.[0]?.quotas['services.count'] || 0}
              </p>
              <p className="text-xs text-muted-foreground">Starter</p>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <Crown className="w-6 h-6 mx-auto mb-2 text-amber-500" />
              <p className="text-sm text-muted-foreground">Services</p>
              <p className="text-xl font-bold">
                {plans?.[1]?.quotas['services.count'] === -1 ? '∞' : plans?.[1]?.quotas['services.count'] || 0}
              </p>
              <p className="text-xs text-muted-foreground">Professional</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}